import { Injectable } from '@angular/core';
import {
  State, Store, Action, StateContext, Selector,
} from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import { NomenclatureBalancesService } from '@core/services/nomenclature-balances.service';
import {
  GetProductListResponse,
  NomenclatureBalance,
  OffsetPagination,
  ProductFilter,
  RemainingProduct,
  RemainingResponse,
} from '@core/interfaces/nomenclature-balances';
import { formatDateWithoutTime } from '@core/helper/request-time-window-helper';
import { BaseSortedEntityListState } from '@core/interfaces/base-entity-list-state.interface';
import { getDefaultSortedEntityListState } from '@core/helper/state-helper';
import { SortInterface } from '@core/interfaces/get-list.interface';
import { ApolloQueryResult } from '@apollo/client/core';
import {
  GetListNomenclatureBalance,
  GetRemaining,
  SetProductFilter,
  SetProductPaginationParams,
  SetProductSort,
} from '../action/nomenclature-balances.action';

export interface NomenclatureBalancesStateModel extends BaseSortedEntityListState<NomenclatureBalance, ProductFilter> {
  remainingProduct: RemainingProduct | null;
}

@State<NomenclatureBalancesStateModel>({
  name: 'NomenclatureBalancesState',
  defaults: {
    ...getDefaultSortedEntityListState<ProductFilter, NomenclatureBalance>(),
    remainingProduct: null,
  },
})
@Injectable()
export class NomenclatureBalancesState {
  constructor(
    private store: Store,
    private nomenclatureBalancesService: NomenclatureBalancesService,
  ) {}

  @Selector()
  static getNomenclatureBalances(state: NomenclatureBalancesStateModel): NomenclatureBalance[] {
    return state.list;
  }

  @Selector()
  static getNomenclatureBalancesTotal(state: NomenclatureBalancesStateModel): number {
    return state.total;
  }

  @Selector()
  static getNomenclatureBalancesRemainingItem(state: NomenclatureBalancesStateModel): RemainingProduct | null {
    return state.remainingProduct;
  }

  @Selector()
  static filter(state: NomenclatureBalancesStateModel): ProductFilter {
    return state.filter;
  }

  @Selector()
  static paginationParams(state: NomenclatureBalancesStateModel): OffsetPagination {
    return state.pagination;
  }

  @Selector()
  static sort(state: NomenclatureBalancesStateModel): SortInterface {
    return state.sort;
  }

  @Action(GetListNomenclatureBalance)
  public getListNomenclatureBalance(
    ctx: StateContext<NomenclatureBalancesStateModel>,
    { params }: GetListNomenclatureBalance,
  ): Observable<ApolloQueryResult<GetProductListResponse>> {
    return this.nomenclatureBalancesService.getProductList(params).pipe(
      tap((res) => {
        const transformedResult = res.data.getListProduct.result.map((item) => ({
          ...item,
          updatedAt: formatDateWithoutTime(item.updatedAt || item.createdAt),
        }));

        ctx.patchState({ total: res.data.getListProduct.total });
        ctx.patchState({ list: transformedResult });
      }),
    );
  }

  @Action(GetRemaining)
  getRemaining(
    ctx: StateContext<NomenclatureBalancesStateModel>,
    { article, pharmacyId }: GetRemaining,
  ): Observable<ApolloQueryResult<RemainingResponse>> {
    return this.nomenclatureBalancesService.getRemaining(article, pharmacyId).pipe(
      tap((res) => {
        if (res && res.data && res.data.getRemaining) {
          const transformedOrder = {
            ...res.data.getRemaining,
            updatedAt: formatDateWithoutTime(res.data.getRemaining.updatedAt || res.data.getRemaining.createdAt),
          };
          ctx.patchState({ remainingProduct: transformedOrder });
        } else {
          ctx.patchState({ remainingProduct: null });
        }
      }),
    );
  }

  @Action(SetProductFilter)
  public setFilter(ctx: StateContext<NomenclatureBalancesStateModel>, { filter }: SetProductFilter): void {
    ctx.patchState({
      filter,
    });
  }

  @Action(SetProductSort)
  public setSort(ctx: StateContext<NomenclatureBalancesStateModel>, { sort }: SetProductSort): void {
    ctx.patchState({
      sort,
    });
  }

  @Action(SetProductPaginationParams)
  public setProductPaginationParams(
    ctx: StateContext<NomenclatureBalancesStateModel>,
    { paginationParams }: SetProductPaginationParams,
  ): void {
    ctx.patchState({
      pagination: paginationParams,
    });
  }
}
